/** @module SiteAdmin/main */
define([
	"underscore",
	"csi",
	"csi/access/crews",
	"csi/comm/GetRequest",
	"csi/config",
	"csi/html",
	"csi/locale",
	"csi/model/image",
	"csi/model/sustainabilityProfile",
	"csi/page",
	"csi/reflection",
	"csi/reflection/reflection",
	"csi/reflectionHints",
	"csi/service/businessObjectService",
	"csi/service/layoutService",
	"csi/sessionConstants",
	"csi/siteDefaultLocale",
	"csi/state",
	"csi/state/Cache",
	"csi/state/rootStore",
	"csi/view",
	"csi/xml",
	"src/c8-platform/type/uom",
	"src/c8-platform/type/value",
	"./SelectSet/SelectSetHelper",
	"./BusinessObject",
	"./Locale",
	"./ManageCrewAction",
	"./SelectSet/GenerateItemsDataPackagePDF",
	"./DirectEditAI"
], function(_, csi, crews, GetRequest, config, html, locale, {ATTR_IMAGES}, { isSustainabilityProfileFormAttribute }, page, reflection, {hasAggregateStoreForAttribute}, reflectionHints, businessObjectService, layoutService,
	sessionConstants, siteDefaultLocale, state, {rootCache}, {featureTogglesStore, routerStore, sessionStore}, csiView, xml, {generateUOMConversionURL}, {isRefValueType}, SelectSetHelper) {

	const _sharedTeamAccessibleRootNodes = {
		Category1: 1,
		Category2: 1,
		Collection: 1,
		ColorPalette: 1,
		EngineeringChange: 1,
		FinalInspectionSecurityGroup: 1,
		MaterialSecurityGroup: 1,
		ProductHierarchy: 1,
		ProductHierarchyFolder: 1,
		Project: 1,
		Season: 1,
		ShapeSecurityGroup: 1,
		ThemeSecurityGroup: 1,
	};

	if (sessionConstants.isLocalUser) {
		locale.registerDisplayNameResolver("CompanyInfo", function(node) {
			return locale.resource("SitePage");
		});
	}

	layoutService.registerCustomTabName("BusinessObject", "MasterAttributeCards", (node) => locale.type(node.RootClassName));
	layoutService.registerCustomTabName("BusinessObject", "RevisionAttributeCards", (node) => locale.type(node.AttributeClassName));

	locale.registerDisplayNameResolver("DocumentGroup", function(node) {
		return locale.resource("Global");
	});

	locale.registerDisplayNameResolver("EnumValue", function(node, cache) {
		var enumList = cache ? cache.getNode(node.__Parent__) : null;
		return enumList ? locale.getEnumName(xml.enumCreate(enumList.$Name, node.$Name)) : node.$Name;
	});

	locale.registerDisplayNameResolver("Locale", function(node) {
		return locale.resource(node.$Name);
	});

	locale.registerDisplayNameResolver(reflection.TYPE_DATA_SETUP, function(node) {
		return locale.type(reflection.TYPE_DATA_SETUP);
	});

	locale.registerDisplayNameResolver(reflection.TYPE_HOME, function(node) {
		return locale.type(reflection.TYPE_HOME);
	});

	locale.registerDisplayNameResolver(reflection.TYPE_SITE, function(node) {
		return locale.type(reflection.TYPE_SITE);
	});

	locale.registerDisplayNameResolver("_CS_Phase", function(node) {
		return locale.resource("Phase" + node.$Name);
	});

	locale.registerDisplayNameResolver("DSLineDivider", function(node) {
		return node.__TextSpan__;
	});

	reflectionHints.setLocationLayoutCallback(reflection.TYPE_SITE, siteDefaultLocale.initialize);
	reflectionHints.setLocationLayoutCallback(reflection.TYPE_BUSINESS_OBJECT, businessObjectService.customizeLayout);
	reflectionHints.setLocationLayoutCallback("SelectSet", SelectSetHelper.customizeLayout);

	/**
	 * Checks for a given type if the Authentication Action is allowed
	 * @param {Context} ctx
	 * @param {Action} action
	 * @param {string} type
	 * @return {boolean}
	 */
	function isAuthenticationActionAllowedByType(ctx, action, type) {
		type = type.toLowerCase();
		const node = ctx.getNode();
		const url = xml.nodeGetURL(node);
		const authType = ctx.cache.getNode(node.Details).AuthType.split("/").pop().toLowerCase();
		const userType = node.UserType;
		return userType == "UserType:User" ? 
			type != authType && _userAuthenticationTypesEnabled(type) : 
			type != authType && _serviceAccountAuthenticationTypesEnabled(type) && !_isInternalUser(url);
	}

	/**
	 * checks if the interactive user authentification type is enabled
	 * @param {string} authType
	 * @return {boolean}
	 */
	function _userAuthenticationTypesEnabled(authType) {
		const userAuthTypes = sessionConstants.userAuthenticationTypesEnabled;
		const authTypes = userAuthTypes.length > 0 ? userAuthTypes.toLowerCase().split(",") : [];
		return authTypes.includes(authType.toLowerCase());
	}

	/**
	 * checks if service user authentification type is enabled
	 * @param {string} authType
	 * @return {boolean}
	 */
	function _serviceAccountAuthenticationTypesEnabled(authType) {
		const serviceUserAuthTypes = sessionConstants.serviceAccountAuthenticationTypesEnabled;
		const authTypes = serviceUserAuthTypes.length > 0 ? serviceUserAuthTypes.toLowerCase().split(",") : [];
		return authTypes.includes(authType.toLowerCase());
	}

	/**
	 * checks if the user is internal or not
	 * @param {CNL} url
	 * @return {boolean}
	 */
	function _isInternalUser(url) {
		return url == "centric://REFLECTION/INSTANCE/User/System";
	}

	return {
		/**
		 * Edit delegation not allow for System or Administrator user, but admin is able to change delegation for other users
		 * @param {Context} ctx
		 * @param {Action} action
		 * @return {boolean}
		 */
		accessActionEditDelegation: function(ctx, action) {
			const user = ctx.getNode();

			if (user.$URL == xml.ADMIN_USER) {
				return false;
			}

			if (xml.enumGetText(user.UserType) != "User") {
				return false;
			}

			return state.get("admin") || (user.$URL == state.get("userUrl"));
		},

		/**
		* Return true if user has write access to node in context
		* @param {Context} ctx
		* @param {Action} action
		* @return {boolean}
		*/
		accessActionWriteAccess(ctx, action) {
			return xml.nodeHasAccess(ctx.getContextNode(), xml.RIGHT_WRITE);
		},

		onAccessTabSharedCrew: function(node, cache) {
			const crewHolder = cache.get(node.__Parent__);
			return _sharedTeamAccessibleRootNodes[crewHolder.$Type];
		},

		onAccessTabProductHierarchyRetailProduct: function(node, cache) {
			const productHierarchy = node.$Type == "ProductHierarchy" ? node : routerStore.getBreadcrumbNode("ProductHierarchy");

			return productHierarchy ? _.contains(config.getSubtypeSetting(productHierarchy.Subtype, "ApplicableProductTypes"), "ProductType:RetailProduct") : false;
		},

		onAccessTabProductHierarchyFood: function(node, cache) {
			const productHierarchy = node.$Type == "ProductHierarchy" ? node : routerStore.getBreadcrumbNode("ProductHierarchy");

			return productHierarchy ? _.contains(config.getSubtypeSetting(productHierarchy.Subtype, "ApplicableProductTypes"), "ProductType:Food") : false;
		},

		onAccessTabProductHierarchyFormulatedMaterial: function(node, cache) {
			const productHierarchy = node.$Type == "ProductHierarchy" ? node : routerStore.getBreadcrumbNode("ProductHierarchy");

			return productHierarchy ? _.contains(config.getSubtypeSetting(productHierarchy.Subtype, "ApplicableProductTypes"), "ProductType:FormulatedMaterial") : false;
		},

		/**
		 * C8FB-1612
		 * To edit Food/Formulated Material on Product Hierarchy, user needs both admin access to Food/FM and edit access to Hierarchy.
		 * @param {Context} ctx
		 * @param {Action} action
		 * @returns {boolean}
		 */
		onAccessActionSelectProductHierarchy: function(ctx, action) {
			const node = ctx.getNode();
			const productAdminRoles = node?.$Type === "Food" ? ["Food Administrator"] : ["Formulated Material Administrator"];
			return crews.isInGlobalRoles(productAdminRoles) &&
				crews.isInGlobalRoles(["Hierarchy Editor", "Hierarchy Administrator", "Hierarchy Library Administrator"]);
		},

		onAccessActionCopyProductHierarchyLevel: function(ctx, action) {
			const node = ctx.getNode();
			if (!node) {
				return false;
			}
			const productHierarchy = node.$Type == "ProductHierarchy" ? node : ctx.cache.getNode(node.HierarchyParents[0]);
			const folderSubtypes = config.getSubtypeSetting(productHierarchy?.Subtype, "ProductHierarchyFolders");
			return node.Level < folderSubtypes?.length || (node.Level == folderSubtypes?.length && crews.isInRoles(["Leaf Folder Administrator"], node));
		},

		onDrawAuditName: function(/*Context*/ctx, /*Heading*/h, /*DOMNode*/refNode) {
			var node = ctx.getNode();
			var parent = ctx.cache.getNode(node.__Parent__, true);
			if (parent) {
				html.setInnerText(refNode, locale.typeId(parent.$Type, node.$Name));
			}
		},

		/**
		 * Only allow access to create 3D document if container allows 3D documents
		 * @param {Context} ctx
		 * @param {Action} action
		 * @return {boolean}
		 */
		onAccessActionCreate3DDocument: function(ctx, action) {
			return sessionConstants.allow3d;
		},

		/**
		 * Only allow access to create 3D material if container allows 3D materials
		 * This will handle access to create on both property view and table view on same function
		 * @param {Context} ctx
		 * @param {Action} action
		 * @return {boolean}
		 */
		onAccessActionCreate3DMaterial: function(ctx, action) {
			var node = ctx.getNode() || ctx.getNodeParent(ctx.getParent());
			var isWritable = true;

			if (node && (node.$Type == "Material" || node.$Type == "MaterialSample")) {
				var commentsNode = ctx.cache.getNode(node.DocumentsAndComments);
				isWritable = xml.nodeHasRight(commentsNode, xml.RIGHT_WRITE);
			}

			return sessionConstants.allow3d && node && reflectionHints.is3DMaterialOwner(node.$Type) && isWritable && !(state.get("userType") == "Supplier" && node.$Type == "SRLineItem");
		},

		/**
		 * Only allow access to set Default 3D Environment if the user has 3D Library Admin and Document Admin roles
		 * @param {Context} ctx
		 * @param {Action} action
		 * @return {boolean}
		 */
		onAccessActionDefault3DEnvironment: function(ctx, action) {
			return crews.isInRoles(["3D Library Administrator"], ctx.getNodeParent()) &&
				crews.isInRoles(["Document Administrator"], ctx.getNodeParent());
		},

		/**
		 * Only allow access if Centric Drive is Enabled
		 * @param {Context} ctx
		 * @param {Action} action
		 * @return {boolean}
		 */
		onAccessActionNewDriveDocument: function(ctx, action) {
			return sessionConstants.isDriveEnabled;
		},

		onAccessActionReadOnlyFileVersion: function(/*Context*/ctx, /*Action*/action) {
			return ctx.isReadOnly();
		},

		onAccessActionMakeInactive: function(/*Context*/ctx, /*Action*/action) {
			// make sure user can activate System, is not already inactive, and is not the admin or yourself
			var node = ctx.getNode();
			var url = xml.nodeGetURL(node);

			return xml.isValidURL(xml.nodeGetAttributeValue(node, "Identity")) &&
				(url != "centric://REFLECTION/INSTANCE/User/System") &&
				(url != "centric://REFLECTION/INSTANCE/User/Administrator") &&
				(url != sessionConstants.userUrl) &&
				crews.isAdmin();
		},

		onAccessActionMakeActive: function(/*Context*/ctx, /*Action*/action) {
			// make sure user can activate System and is not already active
			var node = ctx.getNode();
			var url = xml.nodeGetURL(node);

			return !xml.isValidURL(xml.nodeGetAttributeValue(node, "Identity")) &&
				(url != "centric://REFLECTION/INSTANCE/User/System") &&
				crews.isAdmin();
		},

		onAccessChangeCredentials: function(/*Context*/ctx, /*Action*/action) {
			// can't change System Credentials
			var node = ctx.getNode();
			var url = xml.nodeGetURL(node);
			let authType = ctx.cache.getNode(ctx.getNode().Details).AuthType;

			return (url != "centric://REFLECTION/INSTANCE/User/System") &&
				!xml.nodeGetAttributeValue(node, "IsLdapUser") &&
				("centric://REFLECTION/INSTANCE/AuthenticationType/Standard" == authType ||
					"centric://REFLECTION/INSTANCE/AuthenticationType/OAuth2" == authType);
		},

		onAccessDeleteFormDefinitionAttribute: function(/*Context*/ctx, /*Action*/action) {
			return !isSustainabilityProfileFormAttribute(ctx.getNode());
		},

		onAccessUserRoles: function(/*Context*/ctx, /*Action*/action) {
			var parent = ctx.cache.getNode(ctx.getParent().__Parent__);

			// C8-26635
			if (parent.$Type == "ProductSource") {
				return false;
			} else {
				return true;
			}
		},

		onAccessChangePassword: function(/*Context*/ctx, /*Action*/action) {
			// can't change password for other users.
			const node = ctx.getNode();
			const url = xml.nodeGetURL(node);

			return (url != "centric://REFLECTION/INSTANCE/User/System") && (url == sessionConstants.userUrl);
		},

		onAccessSelectSetHierarchy: function(/*Context*/ctx, /*Action*/action) {
			var node = ctx.getParent();
			return (node.$Type == "Season" || node.$Type == "Category1" || node.$Type == "Category2" || node.$Type == "Collection")
				&& crews.isInRoles(['Select Set Admin'], node);
		},

		onAccessNewSelectSetHierarchy: function(/*Context*/ctx, /*Action*/action) {
			var node = ctx.getParent();
			return node.$Type != "Season";
		},

		onAccessSyncKeycloakUsers: function(ac) {
			return sessionConstants.keycloakEnabled;
		},

		onAccessConvertAuthenticationTypeStandard: function(/*Context*/ctx, /*Action*/action) {
			return isAuthenticationActionAllowedByType(ctx, action, "Standard");
		},

		onAccessConvertAuthenticationTypeOAuth2: function(/*Context*/ctx, /*Action*/action) {
			return sessionConstants.keycloakEnabled && isAuthenticationActionAllowedByType(ctx, action, "OAuth2");
		},

		onAccessConvertAuthenticationTypeOAuth2Service: function(/*Context*/ctx, /*Action*/action) {
			return isAuthenticationActionAllowedByType(ctx, action, "OAuth2Service");
		},

		onAccessConvertAuthenticationTypeLDAP: function(/*Context*/ctx, /*Action*/action) {
			return isAuthenticationActionAllowedByType(ctx, action, "LDAP");
		},

		onAccessConvertAuthenticationTypeSAML2: function(/*Context*/ctx, /*Action*/action) {
			return isAuthenticationActionAllowedByType(ctx, action, "SSO");
		},

		onAccessConvertAuthenticationTypeHybrid: function(/*Context*/ctx, /*Action*/action) {
			return isAuthenticationActionAllowedByType(ctx, action, "Hybrid");
		},

		/**
		 * Only allow access if Factory Direct Development feature flag is enabled
		 * @return {boolean}
		 */
		onAccessFactoryDirectDevelopment: function() {
			return featureTogglesStore.ENABLE_FACTORY_DIRECT_DEVELOPMENT;
		},

		onActionExportUsers: function(ac) {
			var node = ac.getParent();
			var cnlCompanyInfo = node.$URL;
			if (node.$Type != xml.TYPE_COMPANY_INFO)
				cnlCompanyInfo = xml.getUrlPath(cnlCompanyInfo, "Child:CompanyInfo");

			var request = new GetRequest("SiteAdmin", "ExportUsers", {
				"URL": cnlCompanyInfo,
				"OutputJSON": 0
			});
			csi.browserWindow(request.toRequest(), "Export");
		},

		onActionExportViewPreferences: function(ac) {
			var request = new GetRequest("NodeProcessor", "ExportQuerySimple", {
				"Qry.Node.Type": "EQ_CS_PreferenceContainer",
				"Dep.Path": "Child:Preferences/Child:Filters",
				"OutputJSON": "0"
			});

			csi.browserWindow(request.toRequest(), "Export");
		},

		/**
		 * Create UserDefaults reference for every host user that does not already have one
		 */
		onActionInitializeUserDefaults: function(ac) {
			var w = ac.m_writer;
			var q = w.createQuery()
				.Type(xml.TYPE_USER)
				.Attr("Active", xml.OP_EQUALS, true)
				.Attr("UserType", xml.OP_EQUALS, "UserType:User")
				.RefAttr(xml.ATTR_PARENT, xml.OP_EQUALS, csi.EMPTY_URL)
				.RefAttr("Defaults", xml.OP_EQUALS, csi.EMPTY_URL);

			ac.m_writer.createChildQuery(q, "UserDefaults", "Defaults");
		},

		onActionNewTypeName: function(ac) {
			// summary: initialize name of new node with its type name if name wasn't already set in a form
			var fd = ac.m_formData;
			var field = fd && fd.getField(xml.ATTR_NAME);
			var name = field && field.getValue();

			if (!fd || !name) {
				ac.m_writer.changeNode(ac.m_urlNode).changeAttribute(xml.ATTR_NAME, xml.ATTR_STRING, locale.type(ac.m_typeNode));
			}
		},

		onActionNewEnum: function(ac) {
			ac.m_writer.changeNode(ac.m_urlNode).addAttribute("Custom", xml.ATTR_BOOLEAN, true);
		},

		onActionTabCareAndCompositionDataSheetSubtypes: function() {
			return featureTogglesStore.CAREANDCOMPOSITION_DATASHEET_USE_REACT_UI;
		},

		/**
		 * Display the tab only if user has the correct access role
		 * @param {C8Node} node
		 */
		onAccessTabExternalAPIConnector: function(node, _) {
			return crews.isInRoles(["External API Administrator"], node);
		},

		onActionToggleUOMConversionActive: function(/*Controller*/ac) {
			const {m_cache, newValue} = ac;
			const uomConversion = m_cache.getNode(ac.m_urlNode, true);
			const uomTypeName = m_cache.getNode(uomConversion.ConversionType, true).$Name;
			const uomExtName = m_cache.getNode(uomConversion.ExternalUOM, true).$Name;
			const uomIntName = m_cache.getNode(uomConversion.InternalUOM, true).$Name;
			const inverseConversionURL = generateUOMConversionURL(uomTypeName, uomExtName, uomIntName);

			// newValue is the Active attribute's boolean value
			if (newValue == true) {
				ac.m_writer.changeNode(inverseConversionURL)
					.changeAttribute("Active", xml.ATTR_BOOLEAN, true);
			} else {
				ac.m_writer.changeNode(inverseConversionURL)
					.changeAttribute("Active", xml.ATTR_BOOLEAN, false);
			}
		},

		/**
		 * Ensure that user's UserDefaults node is always added to the rootCache
		 * @param {Controller} ac
		 */
		postActionManageUserDefaults(ac) {
			if (ac.m_urlParent === sessionStore.userUrl) {
				rootCache.add(ac.getNode());
			}
		},

		postActionRevisionProperty: function(ac) {
			// summary: update revision table in phase view drop-down with new data
			var view = ac.widget.viewParent;
			if (view) {
				var row = view.getRefNodeByUrl(ac.m_urlNode);
				if (row) {
					view.cacheView.put(ac.getNode());
					view.redrawNode(row);
				}
			}
		},

		postActionManufacturerCodes: function(/*Controller*/ac) {
			// When the ManufacturerCodes code list gets changed update
			// DefaultManufacturerCode attribute in the "GTIN" properties view
			var view = ac.widget.viewParent ? ac.widget.viewParent.getChildView("GTIN") : null;
			if (view) {
				var h = view.defView.findHeading("DefaultManufacturerCode");
				view.cacheView.put(ac.m_cache.getNode(view.getViewURL()));
				view.refreshHeading(h);
				view.refreshHeadingStore(h);
			}
		},

		postActionToggleUOMConversionActive: function(/*Controller*/ac) {
			const {m_cache, widget} = ac;
			const uomConversion = m_cache.getNode(ac.m_urlNode, true);
			const uomTypeName = m_cache.getNode(uomConversion.ConversionType, true).$Name;
			const uomExtName = m_cache.getNode(uomConversion.ExternalUOM, true).$Name;
			const uomIntName = m_cache.getNode(uomConversion.InternalUOM, true).$Name;
			const inverseConversionURL = generateUOMConversionURL(uomTypeName, uomExtName, uomIntName);

			const row = widget.getRefNodeByUrl(inverseConversionURL);
			// Refresh inverse conversion node so Active checkbox and ReadOnly properties are updated
			if (row) {
				widget._refreshRow(row);
			}
		},
		/**
		 * Refresh RoleUsers and UserRoles taps after the action is completed
		 * @param {Controller} ac
		 * @returns {void}
		 */
		postActionRefreshCompanyTabs: function(ac) {
			const childViewRoleUsers = csiView.getView("RoleUsers");
			const childViewUserRoles = csiView.getView("UserRoles");
			if (childViewRoleUsers) {
				childViewRoleUsers.refreshWidget();
			}
			if (childViewUserRoles) {
				childViewUserRoles.refreshWidget();
			}
		},

		//============================== Crew ==============================

		onDrawCrewAssignedAt: function(/*Context*/ctx, /*Heading*/h, /*DOMNode*/refNode) {
			html.clear(refNode);
			this.drawCrewAssignedAt(refNode, ctx.getNode(), ctx.cache);
		},

		drawCrewAssignedAt: function(/*DOMNode*/refNode, /*Node*/node, /*Cache*/cache) {
			var members = cache.getChildren(node, "AssignedUsers", true);
			if (members && members.length > 0) {
				var crew = cache.getNode(node.__Parent__, true);
				var parent = cache.getNode(crew.__Parent__, true);
				if (parent) {
					if (refNode.firstChild) {
						html.text(refNode, ", ");
					}

					html.text(refNode, locale.type(parent.$Type));
				}
				return;
			}

			members = cache.getChildren(node, "__CrewMembers__", null, true);
			if (members && members.length > 0) {
				var parentAssignments = cache.getChildren(node, "ParentAssignments", true);
				for (var i = 0; i < parentAssignments.length; ++i) {
					var parentAssignment = parentAssignments[i];
					if (parentAssignment)
						this.drawCrewAssignedAt(refNode, parentAssignments[i], cache);
				}
				return;
			}
		},

		onAccessCrewAssignmentDelete: function(/*Context*/ctx, /*Action*/action) {
			return true;
		},

		onAccessRemoveUserFromGlobalRole: function(/*Context*/ctx, /*Action*/action) {
			// DE26538: for SiteAdministrator group dissalow disselecting Administrator
			if (ctx.getParentURL() == xml.ADMIN_USER) {
				var group = ctx.getNode()
				if (group && group.$URL == xml.ROLE_ADMIN)
					return false;
			}
			// C8-19223: Remove icon should be available only for user with Team Manager role
			return crews.isInRoles(["Team Manager"], ctx.getContextNode());
		},

		onAccessRemoveUserFromCrewAssignment: function(ctx, action) {
			// C8-19223: Remove icon should be available only for user with Team Manager role
			return crews.isInRoles(["Team Manager"], ctx.getContextNode());
		},

		onAccessCheckForUserType: function(ctx, action) {
			const user = ctx.getContextNode();
			return user && xml.enumGetText(user.UserType) == "User";
		},


		//============================== Roles ==============================

		onDrawPlayedSecurityRoles: function(/*Context*/ctx, /*Heading*/h, /*DOMNode*/refNode) {
			// summary: for the current node, find the node from the cache references this node through 'SubGroups' relationship
			const roleURL = ctx.getNodeURL();
			const roles = ctx.cache.getParents(roleURL, "SubGroups") || [];
			const rolesPlayedNames = roles.filter((node) => node.UsedByACL).map((node) => locale.getNodeName(node));
			html.text(refNode, csi.sort(rolesPlayedNames).join(", "));
		},
		//============================== Issues ==============================

		preActionOnIssueCopy: function(ac) {
			var node = ac.m_cache.getNode("centric://REFLECTION/INSTANCE/IssueConfiguration/Configuration", true);

			if (!node.IssueNumberAutoGenerate) {
				ac.m_action.forceRename = -1; // enforce default copy behavior
			}
			return true;
		},

		//============================== SelectSet Actions ==============================

		_hasPDFStatus: function(node) {
			return node["PDF.Status"] != undefined;
		},

		_isProduct: function(node) {
			return (node.Subtype == "centric://REFLECTION/INSTANCE/SelectSetSubtype/Style")
				|| (node.Subtype == "centric://REFLECTION/INSTANCE/SelectSetSubtype/Material");
		},

		/**
		 * Don't allow action if ProductHierarchySubtype is published
		 * @param {Context} ctx
		 * @param {Action} action
		 */
		onAccessActionEditLevels: function(ctx, action) {
			return !ctx.getNodeParent().Published;
		},

		onAccessActionGeneratePDF: function(/*Context*/ctx, /*Action*/action) {
			// summary: if file is still being generated, then do not allow to generate again
			return !this._hasPDFStatus(ctx.getNode());
		},

		onAccessActionDPGeneratePDF: function(/*Context*/ctx, /*Action*/action) {
			// summary: do not allow Data Package PDF generation from popup (or if still being generated)
			return !ctx.bPopup && this.onAccessActionGeneratePDF(ctx, action);
		},

		onAccessActionDefaultSkinTheme: function(/*Context*/ctx, /*Action*/action) {
			// summary: do not allow change sking theme if the user doesn't have Team manager role
			return crews.isInGlobalRoles(["Team Manager"]);
		},

		/**
		 * If the node type is a ref and the relationship from the source node to the target node has an aggregate store, show the action
		 * @param {Context} ctx 
		 * @param {Action} action 
		 * @returns 
		 */
		onAccessActionGetAggregateStoreInfo(ctx, action) {
			const node = ctx.getNode();
			return isRefValueType(node[reflection.ATTR_VALUETYPE]) && hasAggregateStoreForAttribute(node.ReflectionAttributeOwner, node.ReflectionAttributeId);
		},

		onAccessActionSSItemGeneratePDF: function(/*Context*/ctx, /*Action*/action) {
			// summary: if file is still being generated, then do not allow to generate again
			var dp = ctx.cache.getNode(ctx.getNode().DataPackage);
			return !dp || !this._hasPDFStatus(dp);
		},

		onAccessActionSSGeneratePDF: function(/*Context*/ctx, /*Action*/action) {
			// summary: if file is being generated or for Style/Material if PDF is being generated for one of the items or if none of the items has PDF, then do not allow to generate
			var node = ctx.getNode();
			if (!node.Items || !node.Items.length) return false;
			return this.onAccessActionGeneratePDF(ctx, action);
		},

		onAccessActionSSItemsGeneratePDF: function(/*Context*/ctx, /*Action*/action) {
			if (!this._isProduct(ctx.getNode())) return false;
			return this.onAccessActionSSGeneratePDF(ctx, action);
		},

		onAccessActionSynchronizeAllLdapServers: function(/*Context*/ctx, /*Action*/action) {
			if (ctx.view.queryTotalCount == 0) {
				return false;
			}
			return crews.isInRoles(["Configuration Editor"], ctx.getNode());
		},

		postActionSelectSetItem: function(ac) {
			var view = ac.widget.viewParent.getChildView("Properties");
			if (view) {
				var ctx = view.getContext();
				ctx.cache.put(ac.getParent());
				view.refreshWidget();
				view.postAction();
			}
		},

		postActionQueryFilter: function(ac) {
			ac.widget.postAction();
			var view = this._getItemsView(ac);
			if (view) {
				var ctx = view.getContext();
				ctx.cache.put(ac.getParent());
				view.refreshWidget();
			}
		},

		postActionRefreshItemsView: function(ac) {
			var view = this._getItemsView(ac);
			if (view) {
				view.refreshWidget();
			}
		},

		postActionDocumentColors: function(ac) {
			const {m_cache, m_response, widget} = ac;
			const documents = m_response.getNodesOfType("Document");
			const rootDocuments = documents.map((document) => m_cache.get(document.VariantSource) || document);
			const urlMap = csi.hashArray(rootDocuments, xml.nodeGetURL, Boolean);

			csiView.refreshAllViewsOnTab("ProductColors");

			return widget.refreshDataRows((row) => urlMap[widget.getRefNodeUrl(row)]);
		},

		postActionRefreshPage(ac) {
			ac.widget.postActionRefresh(ac);
			page.refreshRootView();
		},

		_getItemsView: function(ac) {
			var viewParent = ac.widget.viewParent;
			var itemsViewId = viewParent && viewParent.defView.defsByKey && viewParent.defView.defsByKey.hasOwnProperty(2) ? viewParent.defView.defsByKey[2].m_id : null;
			if (itemsViewId) {
				return viewParent.getChildView(itemsViewId);
			}
			return null;
		},

		onAccessCheckProofStatus: function(/*Context*/ctx, /*Action*/action) {
			const docContainer = ctx.cache.getNode(ctx.getNode().DocumentsAndComments);
			const hide = docContainer && _.any(ctx.cache.getNodes(docContainer.Documents), function(doc) {
				const rev = doc && doc.CurrentRevision && ctx.cache.getNode(doc.CurrentRevision);
				const docState = rev && rev.State;
				const proofStatus = rev && rev.ProofStatus;

				const isInProofingState = (docState == "Revision.State:PROOFING");
				const invalidProofingStatus = (docState == "Revision.State:DRAFT" && (proofStatus == "ProofDecisionStatus:ChangesRequired" || proofStatus == "ProofDecisionStatus:NewVersionAvailable"))
				return isInProofingState || invalidProofingStatus;
			});
			return !hide;
		},

		onAccessActionImageEditorUnlinkSwitch: function(ctx, action) {
			const node = ctx.getNode();
			const nodeIsWriteable = xml.nodeHasAccess(node, xml.RIGHT_WRITE);
			const imagesAreWriteable = !xml.nodeAttributeHasFlag(node, ATTR_IMAGES, xml.ATTR_FLAG_NOEDIT);
			return nodeIsWriteable && imagesAreWriteable;
		},

		/**
		 * Get the Crew or AssignedAgentCrew of the Online Supplier base on isForAssignedAgentCrew flag
		 * @param {ICache} cache 
		 * @param {C8Node} supplierCrew
		 * @param {boolean} isForAssignedAgentCrew 
		 * @returns {cnl} Crew or AssignedAgentCrew cnl of the Online Supplier
		 */
		getOnlineSupplierCrew: function(cache, supplierCrew, isForAssignedAgentCrew) {
			return isForAssignedAgentCrew ? cache.getNode(supplierCrew.__Parent__).AssignedAgentCrew : supplierCrew.$URL;
		},
	};
});
